import React, { useState, useEffect, useCallback } from 'react';
import { AuditLog, EntityType, AuditAction } from '../../types';
import { getAuditLogs, AuditLogFilters, getSyncHistory, SyncHistoryItem, triggerFullSync, getPartnerCenterAuthUrl } from '../../services/api';
import HelpTooltip from '../common/HelpTooltip';
import TenantMatcher from '../TenantMatcher/TenantMatcher';
import { Filter, Clock, User, FileText, RefreshCw, CheckCircle, XCircle, AlertTriangle, Play, Building2, Link, ChevronDown, ChevronUp } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';

type TabType = 'audit' | 'sync';

export default function AuditLogsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('audit');
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [syncHistory, setSyncHistory] = useState<SyncHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingTenantIds, setSyncingTenantIds] = useState(false);
  const [expandedSyncId, setExpandedSyncId] = useState<string | null>(null);
  const [filters, setFilters] = useState<AuditLogFilters>({
    page: 1,
    pageSize: 50,
  });

  // Calculate next scheduled sync (every 4 hours from last sync)
  const getNextScheduledSync = () => {
    const lastFullSync = syncHistory.find(s => s.syncType === 'full' || s.syncType === 'pax8');
    if (lastFullSync) {
      const lastSyncTime = new Date(lastFullSync.startTime);
      // Find next 4-hour interval (0, 4, 8, 12, 16, 20)
      const hours = lastSyncTime.getHours();
      const nextSlot = Math.ceil(hours / 4) * 4;
      const nextSync = new Date(lastSyncTime);
      nextSync.setHours(nextSlot >= 24 ? nextSlot - 24 : nextSlot, 0, 0, 0);
      if (nextSync <= new Date()) {
        nextSync.setHours(nextSync.getHours() + 4);
      }
      return nextSync;
    }
    // Default: next 4-hour slot
    const now = new Date();
    const hours = now.getHours();
    const nextSlot = Math.ceil(hours / 4) * 4;
    const nextSync = new Date(now);
    nextSync.setHours(nextSlot >= 24 ? nextSlot - 24 : nextSlot, 0, 0, 0);
    if (nextSync <= now) {
      nextSync.setHours(nextSync.getHours() + 4);
    }
    return nextSync;
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    const toastId = toast.loading('Running full sync (Pax8 + tenant IDs)...');

    try {
      const result = await triggerFullSync();
      toast.success(
        `Sync completed! ${result.summary.pax8.clients} clients, ${result.summary.pax8.subscriptions} subscriptions.${result.summary.tenantIdsPopulated > 0 ? ` ${result.summary.tenantIdsPopulated} tenant IDs auto-populated!` : ''}`,
        { id: toastId, duration: 5000 }
      );
      // Refresh sync history
      fetchSyncHistory();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Sync failed', { id: toastId });
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncTenantIds = async () => {
    setSyncingTenantIds(true);
    const toastId = toast.loading('Opening Partner Center sign-in...');

    try {
      const { authUrl } = await getPartnerCenterAuthUrl();

      // Open popup for Partner Center sign-in
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        authUrl,
        'PartnerCenterAuth',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
      );

      if (!popup) {
        toast.error('Popup blocked. Please allow popups for this site.', { id: toastId });
        setSyncingTenantIds(false);
        return;
      }

      toast.loading('Waiting for Partner Center sign-in...', { id: toastId });

      // Listen for the callback message
      const handleMessage = (event: MessageEvent) => {
        if (event.data?.type === 'PARTNER_CENTER_CALLBACK') {
          window.removeEventListener('message', handleMessage);
          setSyncingTenantIds(false);

          if (event.data.success) {
            toast.success(
              `Tenant ID sync complete! ${event.data.updated} clients updated, ${event.data.notMatched} not matched.`,
              { id: toastId, duration: 6000 }
            );
            // Refresh sync history
            fetchSyncHistory();
          } else {
            toast.error(event.data.message || 'Sync failed', { id: toastId });
          }
        }
      };

      window.addEventListener('message', handleMessage);

      // Also check if popup is closed without completing
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          setSyncingTenantIds(false);
          // Only show cancelled if we didn't get a success message
          if (syncingTenantIds) {
            toast.dismiss(toastId);
          }
        }
      }, 500);

    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to start sync', { id: toastId });
      setSyncingTenantIds(false);
    }
  };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getAuditLogs(filters);
      setLogs(result.items);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchSyncHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getSyncHistory(50);
      setSyncHistory(result.items);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch sync history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'audit') {
      fetchLogs();
    } else {
      fetchSyncHistory();
    }
  }, [activeTab, fetchLogs, fetchSyncHistory]);

  const handleFilterChange = (key: keyof AuditLogFilters, value: string | undefined) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
      page: 1,
    }));
  };

  const clearFilters = () => {
    setFilters({ page: 1, pageSize: 50 });
  };

  const getActionBadgeClass = (action: AuditAction) => {
    switch (action) {
      case 'create':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'update':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'delete':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'sync':
      case 'run':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'flag':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'resolve':
        return 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400';
      case 'login':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'view':
      case 'list':
        return 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400';
      case 'assign-license':
      case 'add-to-group':
      case 'enable':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'remove-license':
      case 'remove-from-group':
      case 'disable':
      case 'offboard':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'reset-password':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const getEntityBadgeClass = (entityType: EntityType) => {
    switch (entityType) {
      case 'client':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400';
      case 'subscription':
        return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400';
      case 'alert':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
      case 'user':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'group':
        return 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-400';
      case 'report-definition':
      case 'report-execution':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'session':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'page':
        return 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const formatChanges = (changes: Record<string, { old: unknown; new: unknown }>) => {
    return Object.entries(changes).map(([key, change]) => (
      <div key={key} className="text-xs mb-1">
        <span className="font-medium text-muted-foreground">{key}:</span>
        <span className="text-red-600 dark:text-red-400 line-through ml-1">
          {JSON.stringify(change.old)}
        </span>
        <span className="text-gray-400 mx-1">&rarr;</span>
        <span className="text-green-600 dark:text-green-400">
          {JSON.stringify(change.new)}
        </span>
      </div>
    ));
  };

  const getSyncStatusIcon = (status: SyncHistoryItem['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'partial':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
    }
  };

  const getSyncTypeBadge = (syncType: SyncHistoryItem['syncType']) => {
    switch (syncType) {
      case 'pax8':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'gdap':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'full':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400';
      case 'tenant-ids':
        return 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400';
      case 'alert-generation':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const formatDuration = (startTime: string, endTime: string) => {
    const start = new Date(startTime).getTime();
    const end = new Date(endTime).getTime();
    const durationMs = end - start;
    if (durationMs < 1000) return `${durationMs}ms`;
    if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
    return `${(durationMs / 60000).toFixed(1)}m`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-foreground">
              Activity Log
            </h1>
            <HelpTooltip
              text="View a complete history of all changes made in the system. Audit Log shows who changed what and when. Sync History shows the results of all Pax8 and GDAP sync operations."
              
            />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Track all changes and sync operations
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('audit')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'audit'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            <FileText className="h-5 w-5 inline-block mr-2 -mt-0.5" />
            Audit Log
          </button>
          <button
            onClick={() => setActiveTab('sync')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'sync'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            <RefreshCw className="h-5 w-5 inline-block mr-2 -mt-0.5" />
            Sync History
          </button>
        </nav>
      </div>

      {/* Filters - Only show for audit tab */}
      {activeTab === 'audit' && (
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-muted-foreground">
              {total} log entries
            </span>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary ${showFilters ? 'bg-muted' : ''}`}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div>
                <label className="label">Entity Type</label>
                <select
                  value={filters.entityType || ''}
                  onChange={(e) => handleFilterChange('entityType', e.target.value as EntityType | '')}
                  className="input"
                >
                  <option value="">All Types</option>
                  <optgroup label="Core">
                    <option value="client">Client</option>
                    <option value="subscription">Subscription</option>
                    <option value="alert">Alert</option>
                  </optgroup>
                  <optgroup label="User Management">
                    <option value="user">User</option>
                    <option value="group">Group</option>
                  </optgroup>
                  <optgroup label="Reports">
                    <option value="report-definition">Report Definition</option>
                    <option value="report-execution">Report Execution</option>
                  </optgroup>
                  <optgroup label="Access">
                    <option value="session">Session (Login)</option>
                    <option value="page">Page (View)</option>
                  </optgroup>
                </select>
              </div>
              <div>
                <label className="label">Action</label>
                <select
                  value={filters.action || ''}
                  onChange={(e) => handleFilterChange('action', e.target.value as AuditAction | '')}
                  className="input"
                >
                  <option value="">All Actions</option>
                  <optgroup label="CRUD">
                    <option value="create">Create</option>
                    <option value="update">Update</option>
                    <option value="delete">Delete</option>
                  </optgroup>
                  <optgroup label="User Management">
                    <option value="assign-license">Assign License</option>
                    <option value="remove-license">Remove License</option>
                    <option value="add-to-group">Add to Group</option>
                    <option value="remove-from-group">Remove from Group</option>
                    <option value="reset-password">Reset Password</option>
                    <option value="enable">Enable</option>
                    <option value="disable">Disable</option>
                    <option value="offboard">Offboard</option>
                  </optgroup>
                  <optgroup label="Reports & Sync">
                    <option value="run">Run Report</option>
                    <option value="sync">Sync</option>
                  </optgroup>
                  <optgroup label="Alerts">
                    <option value="flag">Flag</option>
                    <option value="resolve">Resolve</option>
                  </optgroup>
                  <optgroup label="Access Tracking">
                    <option value="login">Login</option>
                    <option value="view">View</option>
                    <option value="list">List</option>
                  </optgroup>
                </select>
              </div>
              <div>
                <label className="label">User Email</label>
                <input
                  type="text"
                  value={filters.userEmail || ''}
                  onChange={(e) => handleFilterChange('userEmail', e.target.value)}
                  className="input"
                  placeholder="Search by email..."
                />
              </div>
              <div className="flex items-end">
                <button onClick={clearFilters} className="btn-secondary w-full">
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Error State */}
      {error && (
        <div className="card p-4 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : activeTab === 'audit' ? (
        /* Audit Log Content */
        <>
          {logs.length === 0 ? (
            <div className="card p-12 text-center">
              <FileText className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-foreground">
                No audit logs found
              </h3>
              <p className="text-muted-foreground mt-2">
                Changes will be logged when you create, update, or delete items.
              </p>
            </div>
          ) : (
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Timestamp
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Action
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Entity
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Changes
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-background divide-y divide-border">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-accent">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center text-sm text-foreground">
                            <Clock className="h-4 w-4 mr-2 text-gray-400" />
                            {format(new Date(log.timestamp), 'MMM d, yyyy h:mm a')}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center text-sm text-foreground">
                            <User className="h-4 w-4 mr-2 text-gray-400" />
                            <span className="truncate max-w-[150px]" title={log.userEmail}>
                              {log.userEmail}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getActionBadgeClass(log.action)}`}>
                            {log.action}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex flex-col">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize w-fit ${getEntityBadgeClass(log.entityType)}`}>
                              {log.entityType}
                            </span>
                            <span className="text-xs text-muted-foreground mt-1 truncate max-w-[120px]" title={log.entityId}>
                              {log.entityId.slice(0, 8)}...
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="max-w-md">
                            {Object.keys(log.changes).length > 0 ? (
                              formatChanges(log.changes)
                            ) : (
                              <span className="text-xs text-gray-400">No details</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Audit Log Pagination */}
          {total > 0 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {((filters.page || 1) - 1) * (filters.pageSize || 50) + 1} to{' '}
                {Math.min((filters.page || 1) * (filters.pageSize || 50), total)} of {total} entries
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => setFilters(prev => ({ ...prev, page: (prev.page || 1) - 1 }))}
                  disabled={(filters.page || 1) <= 1}
                  className="btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setFilters(prev => ({ ...prev, page: (prev.page || 1) + 1 }))}
                  disabled={(filters.page || 1) * (filters.pageSize || 50) >= total}
                  className="btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Sync History Content */
        <>
          {/* Sync Control Panel */}
          <div className="card p-4 mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleSyncNow}
                  disabled={syncing}
                  className={`btn-primary flex items-center gap-2 ${syncing ? 'opacity-75 cursor-not-allowed' : ''}`}
                >
                  {syncing ? (
                    <>
                      <RefreshCw className="h-5 w-5 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <Play className="h-5 w-5" />
                      Sync Now
                    </>
                  )}
                </button>
                <HelpTooltip
                  text="Runs a full sync of Pax8 clients & subscriptions, and syncs MS Direct subscriptions for clients with tenant IDs."
                />
                <button
                  onClick={handleSyncTenantIds}
                  disabled={syncingTenantIds}
                  className={`btn-secondary flex items-center gap-2 ${syncingTenantIds ? 'opacity-75 cursor-not-allowed' : ''}`}
                >
                  {syncingTenantIds ? (
                    <>
                      <RefreshCw className="h-5 w-5 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <Building2 className="h-5 w-5" />
                      Sync Tenant IDs
                    </>
                  )}
                </button>
                <HelpTooltip
                  text="Opens Partner Center sign-in to fetch all customer tenant IDs and auto-match them to your Pax8 clients."
                />
              </div>
              <div className="text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>
                    Auto-sync every 4 hours • Next: {formatDistanceToNow(getNextScheduledSync(), { addSuffix: true })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Manual Tenant Matching */}
          <div className="card p-4 mb-4">
            <div className="flex items-center gap-2 mb-4">
              <Link className="h-5 w-5 text-gray-400" />
              <h3 className="text-lg font-medium text-foreground">Manual Tenant Matching</h3>
              <HelpTooltip text="Link Pax8 clients to Partner Center customers manually when automatic matching fails due to different company names." />
            </div>
            <TenantMatcher onRefresh={fetchSyncHistory} />
          </div>

          {syncHistory.length === 0 ? (
            <div className="card p-12 text-center">
              <RefreshCw className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-foreground">
                No sync history found
              </h3>
              <p className="text-muted-foreground mt-2">
                Sync operations will be logged here when you run Pax8 or GDAP syncs.
              </p>
            </div>
          ) : (
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Start Time
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Duration
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Clients
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Subscriptions
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Errors
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-background divide-y divide-border">
                    {syncHistory.map((sync) => (
                      <React.Fragment key={sync.id}>
                      <tr className="hover:bg-accent">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center">
                            {getSyncStatusIcon(sync.status)}
                            <span className="ml-2 text-sm capitalize text-foreground">
                              {sync.status}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium uppercase ${getSyncTypeBadge(sync.syncType)}`}>
                            {sync.syncType}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center text-sm text-foreground">
                            <Clock className="h-4 w-4 mr-2 text-gray-400" />
                            {format(new Date(sync.startTime), 'MMM d, yyyy h:mm a')}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">
                          {formatDuration(sync.startTime, sync.endTime)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">
                          {sync.clientsProcessed}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">
                          {sync.subscriptionsProcessed}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {sync.errorsEncountered > 0 ? (
                            <button
                              onClick={() => setExpandedSyncId(expandedSyncId === sync.id ? null : sync.id)}
                              className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors cursor-pointer"
                            >
                              {sync.errorsEncountered} errors
                              {expandedSyncId === sync.id ? (
                                <ChevronUp className="h-3 w-3" />
                              ) : (
                                <ChevronDown className="h-3 w-3" />
                              )}
                            </button>
                          ) : (
                            <span className="text-sm text-gray-400">None</span>
                          )}
                        </td>
                      </tr>
                      {/* Expanded error details row */}
                      {expandedSyncId === sync.id && sync.errorDetails && sync.errorDetails.length > 0 && (
                        <tr className="bg-red-50 dark:bg-red-900/10">
                          <td colSpan={6} className="px-4 py-3">
                            <div className="text-sm">
                              <p className="font-medium text-red-800 dark:text-red-300 mb-2">
                                Error Details:
                              </p>
                              <ul className="list-disc list-inside space-y-1 text-red-700 dark:text-red-400">
                                {sync.errorDetails.map((error, idx) => (
                                  <li key={idx} className="text-xs break-all">
                                    {error}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sync History Count */}
          {syncHistory.length > 0 && (
            <div className="text-sm text-muted-foreground">
              Showing {syncHistory.length} sync operations
            </div>
          )}
        </>
      )}
    </div>
  );
}
